import { supabase } from "~/integrations/supabase/client";
import type {
  RecipeCostBreakdown,
  RecipeFilters,
  RecipeFormData,
  RecipeIngredient,
  RecipeIngredientFormData,
  SkuRecipe,
} from "~/types/production";
import { clpToUsd, costPerKgClp } from "~/utils/oilConversions";

export const recipeService = {
  /**
   * Get all recipes with optional filtering
   */
  async getAll(filters?: RecipeFilters): Promise<SkuRecipe[]> {
    let query = supabase
      .from("sku_recipes")
      .select(
        `
        *,
        product:products(*),
        oil_type:oil_types(*),
        ingredients:recipe_ingredients(
          *,
          raw_material:raw_materials(*),
          oil_type:oil_types(*)
        )
      `
      )
      .is("deleted_at", null)
      .order("name", { ascending: true });

    if (filters?.product_id) {
      query = query.eq("product_id", filters.product_id);
    }

    if (filters?.is_default !== undefined) {
      query = query.eq("is_default", filters.is_default);
    }

    if (filters?.search) {
      query = query.ilike("name", `%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as SkuRecipe[];
  },

  /**
   * Get a single recipe by ID with all ingredients
   */
  async getById(id: string): Promise<SkuRecipe> {
    const { data, error } = await supabase
      .from("sku_recipes")
      .select(
        `
        *,
        product:products(*),
        oil_type:oil_types(*),
        ingredients:recipe_ingredients(
          *,
          raw_material:raw_materials(*),
          oil_type:oil_types(*)
        )
      `
      )
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as SkuRecipe;
  },

  /**
   * Get the default recipe for a product
   */
  async getDefaultForProduct(productId: string): Promise<SkuRecipe | null> {
    const { data, error } = await supabase
      .from("sku_recipes")
      .select(
        `
        *,
        product:products(*),
        oil_type:oil_types(*),
        ingredients:recipe_ingredients(
          *,
          raw_material:raw_materials(*),
          oil_type:oil_types(*)
        )
      `
      )
      .eq("product_id", productId)
      .eq("is_default", true)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) throw error;
    return data as SkuRecipe | null;
  },

  /**
   * Create a new recipe with ingredients
   */
  async create(data: RecipeFormData): Promise<SkuRecipe> {
    // If this is set as default, unset any existing default for this product
    if (data.is_default) {
      await supabase
        .from("sku_recipes")
        .update({ is_default: false })
        .eq("product_id", data.product_id)
        .eq("is_default", true)
        .is("deleted_at", null);
    }

    // Create the recipe (oil_type_id is null - oils are added as ingredients)
    const { data: recipe, error: recipeError } = await supabase
      .from("sku_recipes")
      .insert({
        product_id: data.product_id,
        oil_type_id: null,
        name: data.name,
        is_default: data.is_default,
        description: data.description || null,
      })
      .select()
      .single();

    if (recipeError) throw recipeError;

    // Create ingredients
    if (data.ingredients && data.ingredients.length > 0) {
      const ingredientsToInsert = data.ingredients.map((ingredient) => ({
        recipe_id: recipe.id,
        raw_material_id: ingredient.raw_material_id || null,
        oil_type_id: ingredient.oil_type_id || null,
        quantity_per_box: ingredient.quantity_per_box,
        waste_factor: ingredient.waste_factor,
        notes: ingredient.notes || null,
      }));

      const { error: ingredientsError } = await supabase
        .from("recipe_ingredients")
        .insert(ingredientsToInsert);

      if (ingredientsError) throw ingredientsError;
    }

    // Return the full recipe with ingredients
    return this.getById(recipe.id);
  },

  /**
   * Update a recipe and its ingredients
   * Uses smart upsert to avoid FK constraint violations when ingredients
   * are referenced by production batches
   */
  async update(id: string, data: Partial<RecipeFormData>): Promise<SkuRecipe> {
    // If setting as default, unset any existing default for this product
    if (data.is_default) {
      const existingRecipe = await this.getById(id);
      await supabase
        .from("sku_recipes")
        .update({ is_default: false })
        .eq("product_id", existingRecipe.product_id)
        .eq("is_default", true)
        .is("deleted_at", null)
        .neq("id", id);
    }

    // Update the recipe (oil_type_id not updated - oils are managed as ingredients)
    const updateData: Record<string, unknown> = {};
    if (data.product_id !== undefined) updateData.product_id = data.product_id;
    if (data.name !== undefined) updateData.name = data.name;
    if (data.is_default !== undefined) updateData.is_default = data.is_default;
    if (data.description !== undefined)
      updateData.description = data.description || null;

    if (Object.keys(updateData).length > 0) {
      const { error: recipeError } = await supabase
        .from("sku_recipes")
        .update(updateData)
        .eq("id", id);

      if (recipeError) throw recipeError;
    }

    // Update ingredients if provided - use smart upsert pattern
    if (data.ingredients !== undefined) {
      // Get existing ingredient IDs for this recipe
      const { data: existingIngredients } = await supabase
        .from("recipe_ingredients")
        .select("id")
        .eq("recipe_id", id);

      const existingIds = new Set(
        existingIngredients?.map((i) => i.id) || []
      );
      const incomingIds = new Set(
        data.ingredients.filter((i) => i.id).map((i) => i.id!)
      );

      // IDs to potentially delete (exist in DB but not in incoming data)
      const idsToDelete = [...existingIds].filter(
        (existingId) => !incomingIds.has(existingId)
      );

      // Ingredients to update (have ID and exist in DB)
      const toUpdate = data.ingredients.filter(
        (i) => i.id && existingIds.has(i.id)
      );

      // Ingredients to insert (no ID = new ingredient)
      const toInsert = data.ingredients.filter((i) => !i.id);

      // 1. Update existing ingredients
      for (const ingredient of toUpdate) {
        const { error } = await supabase
          .from("recipe_ingredients")
          .update({
            raw_material_id: ingredient.raw_material_id || null,
            oil_type_id: ingredient.oil_type_id || null,
            quantity_per_box: ingredient.quantity_per_box,
            waste_factor: ingredient.waste_factor,
            notes: ingredient.notes || null,
          })
          .eq("id", ingredient.id);

        if (error) throw error;
      }

      // 2. Insert new ingredients
      if (toInsert.length > 0) {
        const ingredientsToInsert = toInsert.map((ingredient) => ({
          recipe_id: id,
          raw_material_id: ingredient.raw_material_id || null,
          oil_type_id: ingredient.oil_type_id || null,
          quantity_per_box: ingredient.quantity_per_box,
          waste_factor: ingredient.waste_factor,
          notes: ingredient.notes || null,
        }));

        const { error: ingredientsError } = await supabase
          .from("recipe_ingredients")
          .insert(ingredientsToInsert);

        if (ingredientsError) throw ingredientsError;
      }

      // 3. Delete removed ingredients (only if not referenced by production batches)
      if (idsToDelete.length > 0) {
        // Check which ingredients are referenced by production batches
        const { data: referenced } = await supabase
          .from("production_batch_ingredients")
          .select("recipe_ingredient_id")
          .in("recipe_ingredient_id", idsToDelete);

        const referencedIds = new Set(
          referenced?.map((r) => r.recipe_ingredient_id) || []
        );
        const safeToDelete = idsToDelete.filter(
          (delId) => !referencedIds.has(delId)
        );

        if (safeToDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from("recipe_ingredients")
            .delete()
            .in("id", safeToDelete);

          if (deleteError) throw deleteError;
        }

        // Log warning if some ingredients couldn't be deleted
        const cantDelete = idsToDelete.filter((delId) =>
          referencedIds.has(delId)
        );
        if (cantDelete.length > 0) {
          console.warn(
            `Cannot delete ${cantDelete.length} ingredient(s) - used in production batches`
          );
        }
      }
    }

    return this.getById(id);
  },

  /**
   * Soft delete a recipe
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from("sku_recipes")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);

    if (error) throw error;
  },

  /**
   * Set a recipe as the default for its product
   */
  async setDefault(id: string): Promise<SkuRecipe> {
    const recipe = await this.getById(id);

    // Unset any existing default for this product
    await supabase
      .from("sku_recipes")
      .update({ is_default: false })
      .eq("product_id", recipe.product_id)
      .eq("is_default", true)
      .is("deleted_at", null);

    // Set this recipe as default
    const { error } = await supabase
      .from("sku_recipes")
      .update({ is_default: true })
      .eq("id", id);

    if (error) throw error;

    return this.getById(id);
  },

  /**
   * Add a single ingredient to a recipe
   */
  async addIngredient(
    recipeId: string,
    ingredient: RecipeIngredientFormData
  ): Promise<RecipeIngredient> {
    const { data, error } = await supabase
      .from("recipe_ingredients")
      .insert({
        recipe_id: recipeId,
        raw_material_id: ingredient.raw_material_id || null,
        oil_type_id: ingredient.oil_type_id || null,
        quantity_per_box: ingredient.quantity_per_box,
        waste_factor: ingredient.waste_factor,
        notes: ingredient.notes || null,
      })
      .select(
        `
        *,
        raw_material:raw_materials(*),
        oil_type:oil_types(*)
      `
      )
      .single();

    if (error) throw error;
    return data as RecipeIngredient;
  },

  /**
   * Update a single ingredient
   */
  async updateIngredient(
    ingredientId: string,
    data: Partial<RecipeIngredientFormData>
  ): Promise<RecipeIngredient> {
    const { data: ingredient, error } = await supabase
      .from("recipe_ingredients")
      .update({
        raw_material_id: data.raw_material_id || null,
        oil_type_id: data.oil_type_id || null,
        quantity_per_box: data.quantity_per_box,
        waste_factor: data.waste_factor,
        notes: data.notes || null,
      })
      .eq("id", ingredientId)
      .select(
        `
        *,
        raw_material:raw_materials(*),
        oil_type:oil_types(*)
      `
      )
      .single();

    if (error) throw error;
    return ingredient as RecipeIngredient;
  },

  /**
   * Delete an ingredient from a recipe
   */
  async deleteIngredient(ingredientId: string): Promise<void> {
    const { error } = await supabase
      .from("recipe_ingredients")
      .delete()
      .eq("id", ingredientId);

    if (error) throw error;
  },

  /**
   * Calculate the cost breakdown for a recipe
   * Uses current prices and exchange rate
   */
  async calculateCost(
    recipeId: string,
    exchangeRate: number
  ): Promise<RecipeCostBreakdown> {
    const recipe = await this.getById(recipeId);

    if (!recipe.ingredients || recipe.ingredients.length === 0) {
      return {
        recipe_id: recipeId,
        oil_cost_usd: 0,
        oil_cost_clp: 0,
        material_cost_clp: 0,
        total_cost_clp: 0,
        total_cost_usd: 0,
        exchange_rate: exchangeRate,
        ingredients: [],
      };
    }

    // Price ingredients from the SAME operational weighted-average cost the batch
    // snapshot uses (productionBatchService.getIngredientPricing): oils =
    // oil_types.average_cost_clp / density (per-kg), materials =
    // raw_materials.average_cost_clp. getById already loads oil_types(*) /
    // raw_materials(*) on each ingredient, so no extra queries are needed and the
    // preview matches the created batch, Stock de Ingredientes, and Softland.
    let totalOilCostClp = 0;
    let totalMaterialCostClp = 0;

    const ingredientBreakdown: RecipeCostBreakdown["ingredients"] = [];

    for (const ingredient of recipe.ingredients) {
      const quantityWithWaste =
        ingredient.quantity_per_box * ingredient.waste_factor;

      if (ingredient.oil_type_id && ingredient.oil_type) {
        // Oil ingredient. average_cost_clp is a per-LITER cost; recipe quantities
        // are in KG, so convert to per-KG (= per-liter / density) before applying.
        const unitCost = costPerKgClp(
          ingredient.oil_type.average_cost_clp ?? 0,
          ingredient.oil_type.density_kg_per_liter || 0.92
        );
        const ingredientCostClp = unitCost * quantityWithWaste;

        totalOilCostClp += ingredientCostClp;

        ingredientBreakdown.push({
          name: ingredient.oil_type.name,
          type: "oil",
          quantity: ingredient.quantity_per_box,
          quantity_with_waste: quantityWithWaste,
          unit: "kg",
          unit_cost: unitCost,
          total_cost: ingredientCostClp,
        });
      } else if (ingredient.raw_material_id && ingredient.raw_material) {
        // Raw material ingredient — same weighted-average source as the snapshot.
        const unitPrice = ingredient.raw_material.average_cost_clp ?? 0;

        const ingredientCostClp = unitPrice * quantityWithWaste;
        totalMaterialCostClp += ingredientCostClp;

        ingredientBreakdown.push({
          name: ingredient.raw_material.name,
          type: "material",
          quantity: ingredient.quantity_per_box,
          quantity_with_waste: quantityWithWaste,
          unit: ingredient.raw_material.unit,
          unit_cost: unitPrice,
          total_cost: ingredientCostClp,
        });
      }
    }

    const totalCostClp = totalOilCostClp + totalMaterialCostClp;
    // Ingredients are CLP-priced (average_cost_clp); derive the USD total from the
    // batch exchange rate for display consistency.
    const totalCostUsd = clpToUsd(totalCostClp, exchangeRate);

    return {
      recipe_id: recipeId,
      oil_cost_usd: clpToUsd(totalOilCostClp, exchangeRate),
      oil_cost_clp: totalOilCostClp,
      material_cost_clp: totalMaterialCostClp,
      total_cost_clp: totalCostClp,
      total_cost_usd: totalCostUsd,
      exchange_rate: exchangeRate,
      ingredients: ingredientBreakdown,
    };
  },
};
